

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Avatar,
  Button,
  TextField,
  Select,
  MenuItem,
  Switch,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  FaArrowLeft,
  FaUserEdit,
  FaEdit,
  FaCheck,
  FaKey,
  FaLock,
  FaUser,
  FaSchool,
  FaGlobe,
  FaEnvelope,
  FaCoins,
  FaGift,
  FaTag,
  FaUsers,
  FaChevronRight,
  FaTrash,
  FaTicketAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { API_URL } from "../config";
import Logo from "../assets/icons/LiteIcon.png";
import { toast } from "react-toastify";

import axios from "axios";

const ProfilePageFile = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    user: {
      username: "",
      email: "",
      country: "",
      is_afliate: "",
    },
  });
  const [countries, setCountries] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
 const [loadingPromo, setLoadingPromo] = useState(false);
  // Form states
  const [newUsername, setNewUsername] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [newIsInst, setNewIsInst] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [referrerName, setReferrerName] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const tokenString = localStorage.getItem("authToken");
      if (!tokenString) throw new Error("No token");

      const token = JSON.parse(tokenString);

      const [countriesRes, profileRes] = await Promise.all([
        fetch(`${API_URL}/admin/countries/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        }),
        fetch(`${API_URL}/student/profile/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        }),
      ]);

      const countriesData = await countriesRes.json();
      const profileData = await profileRes.json();

      if (countriesRes.ok && profileRes.ok) {
        const countryName =
          countriesData.find((c) => c.id === profileData.country)?.name ||
          "Unknown";
        setCountries(countriesData);
        setProfile({ ...profileData, countryName });

        // Init edit form
        setNewUsername(profileData.user.username);
        setNewCountry(profileData.country || "");
        setNewIsInst(profileData.is_inst || false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSavePromo = async () => {
    if (!promoCode.trim()) return;

    try {
      setLoadingPromo(true);
      setError(null);

      const tokenString = localStorage.getItem("authToken");
      if (!tokenString) throw new Error("No token");

      const token = JSON.parse(tokenString);
      const response = await axios.post(
        `${API_URL}/student/save-promo-code/`,
        {
          promo_code: promoCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token.access}`,
          },
        },
      );

      // Success
      setReferrerName(response.data.referrer_name);
      setPromoCode("");
    } catch (err) {
      // setError(err.response?.data?.message || "Failed to apply promo code");
      toast.error(err.response?.data?.message || "Failed to apply promo code")
    } finally {
      setLoadingPromo(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const tokenString = localStorage.getItem("authToken");
      if (!tokenString) throw new Error("No token");

      const token = JSON.parse(tokenString);
      const res = await fetch(`${API_URL}/student/profile/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: newUsername,
          country: newCountry,
          is_inst: newIsInst,
        }),
      });

      if (res.ok) {
        toast.success("Profile updated!");
        setEditMode(false);
        fetchData();
      } else {
        toast.error("Update failed");
      }
    } catch (err) {
      toast.error("Error saving profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword) return toast.error("Fill both fields");
    setSaving(true);
    try {
      const tokenString = localStorage.getItem("authToken");
      if (!tokenString) throw new Error("No token");

      const token = JSON.parse(tokenString);
      const res = await fetch(`${API_URL}/change-password/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });

      if (res.ok) {
        toast.error("Password changed successfully!");
        setChangePasswordMode(false);
        setOldPassword("");
        setNewPassword("");
      } else {
        toast.error("Failed to change password");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

 return (
   <Box
     sx={{
       minHeight: "100vh",
       background:
         "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
       pb: 10,
     }}
   >
     {/* ── Hero Header ───────────────────────────────────────────────────── */}
     <Box
       sx={{
         position: "relative",
         background: "linear-gradient(135deg, #0c1a2e 0%, #0a1628 100%)",
         pt: { xs: 6, md: 10 },
         pb: { xs: 10, md: 14 },
         textAlign: "center",
         overflow: "hidden",
       }}
     >
       {/* Dot grid */}
       <Box
         sx={{
           position: "absolute",
           inset: 0,
           opacity: 0.04,
           backgroundImage:
             "radial-gradient(circle, #fff 1px, transparent 1px)",
           backgroundSize: "28px 28px",
           pointerEvents: "none",
         }}
       />
       {/* Glow blob */}
       <Box
         sx={{
           position: "absolute",
           width: 400,
           height: 400,
           borderRadius: "50%",
           background:
             "radial-gradient(circle, rgba(2,136,209,0.2) 0%, transparent 70%)",
           top: "50%",
           left: "50%",
           transform: "translate(-50%, -50%)",
           pointerEvents: "none",
         }}
       />

       {/* Back button */}
       <IconButton
         onClick={() => navigate(-1)}
         sx={{
           position: "absolute",
           left: { xs: 12, md: 24 },
           top: { xs: 16, md: 24 },
           color: "#fff",
           background: "rgba(255,255,255,0.08)",
           border: "1px solid rgba(255,255,255,0.15)",
           borderRadius: 2,
           "&:hover": { background: "rgba(255,255,255,0.15)" },
         }}
       >
         <FaArrowLeft size={16} />
       </IconButton>

       {/* Logo + title */}
       <Box sx={{ position: "relative", zIndex: 1 }}>
         <img
           src={Logo}
           alt="Logo"
           style={{ height: 56, marginBottom: 16, borderRadius: 12 }}
         />
         <Typography
           sx={{
             fontWeight: 900,
             fontSize: { xs: "1.6rem", md: "2rem" },
             color: "#fff",
             letterSpacing: "-0.5px",
           }}
         >
           My Profile
         </Typography>
         <Typography
           variant="caption"
           sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 600 }}
         >
           Manage your account settings
         </Typography>
       </Box>
     </Box>

     <Container
       maxWidth="md"
       sx={{ mt: { xs: -6, md: -8 }, position: "relative", zIndex: 1 }}
     >
       {/* ── Avatar card — floats over header ──────────────────────────── */}
       <Paper
         elevation={0}
         sx={{
           borderRadius: 3,
           overflow: "hidden",
           border: "1px solid rgba(2,136,209,0.15)",
           boxShadow: "0 8px 32px rgba(2,136,209,0.12)",
           background: "#fff",
           mb: 3,
         }}
       >
         <Box
           sx={{
             px: 3,
             pt: 4,
             pb: 3,
             textAlign: "center",
             background:
               "linear-gradient(135deg, rgba(2,136,209,0.06), rgba(14,165,233,0.03))",
             borderBottom: "1px solid #e2e8f0",
           }}
         >
           <Avatar
             src="/profile-placeholder.jpg"
             sx={{
               width: 88,
               height: 88,
               mx: "auto",
               mb: 2,
               border: "3px solid rgba(2,136,209,0.3)",
               boxShadow: "0 4px 20px rgba(2,136,209,0.2)",
               background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
               fontSize: "2rem",
               fontWeight: 800,
             }}
           >
             {profile.user.username?.[0]?.toUpperCase()}
           </Avatar>

           <Typography
             sx={{
               fontWeight: 900,
               color: "#0c1a2e",
               fontSize: "1.25rem",
               letterSpacing: "-0.3px",
               mb: 0.5,
             }}
           >
             {profile.user.username || "Student"}
           </Typography>
           <Typography variant="caption" sx={{ color: "#64748b" }}>
             {profile.user.email}
           </Typography>

           <Box mt={2}>
             <Box
               sx={{
                 display: "inline-flex",
                 alignItems: "center",
                 gap: 1,
                 px: 2.5,
                 py: 0.9,
                 borderRadius: 99,
                 background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
                 boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
               }}
             >
               <FaCoins size={13} color="#fff" />
               <Typography
                 sx={{ fontWeight: 800, fontSize: "0.88rem", color: "#fff" }}
               >
                 {profile.coin_num || 0} Coins
               </Typography>
             </Box>
           </Box>
         </Box>

         {/* Info row */}
         <Stack
           direction={{ xs: "column", sm: "row" }}
           divider={
             <Box
               sx={{
                 width: { sm: 1 },
                 height: { xs: 1, sm: "auto" },
                 background: "#f1f5f9",
               }}
             />
           }
           sx={{ px: 0 }}
         >
           {[
             {
               icon: <FaEnvelope size={13} color="#0288D1" />,
               label: "Email",
               value: profile.user.email,
             },
             {
               icon: <FaGlobe size={13} color="#7c3aed" />,
               label: "Country",
               value: profile.countryName || "Not set",
             },
             {
               icon: <FaSchool size={13} color="#059669" />,
               label: "Account Type",
               value: profile.is_inst ? "Institution" : "Student",
             },
           ].map((item, i) => (
             <Box
               key={i}
               sx={{
                 flex: 1,
                 px: 2.5,
                 py: 2,
                 textAlign: { xs: "left", sm: "center" },
               }}
             >
               <Stack
                 direction="row"
                 alignItems="center"
                 justifyContent={{ sm: "center" }}
                 spacing={1}
                 mb={0.25}
               >
                 {item.icon}
                 <Typography
                   sx={{
                     fontSize: "0.62rem",
                     fontWeight: 700,
                     color: "#94a3b8",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                   }}
                 >
                   {item.label}
                 </Typography>
               </Stack>
               <Typography
                 sx={{ fontWeight: 700, color: "#0c1a2e", fontSize: "0.88rem" }}
               >
                 {item.value}
               </Typography>
             </Box>
           ))}
         </Stack>
       </Paper>

       <Stack spacing={3}>
         {/* ── Edit Profile ────────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(2,136,209,0.15)",
             boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
             background: "#fff",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             justifyContent="space-between"
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: editMode ? "1px solid #e2e8f0" : "none",
               background:
                 "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
             }}
           >
             <Stack direction="row" alignItems="center" spacing={1.5}>
               <Box
                 sx={{
                   width: 32,
                   height: 32,
                   borderRadius: 1.5,
                   background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
                 }}
               >
                 <FaUserEdit size={13} color="#fff" />
               </Box>
               <Box>
                 <Typography
                   sx={{
                     fontWeight: 800,
                     color: "#0c1a2e",
                     fontSize: "0.92rem",
                   }}
                 >
                   Edit Profile
                 </Typography>
                 <Typography variant="caption" sx={{ color: "#64748b" }}>
                   Update your personal information
                 </Typography>
               </Box>
             </Stack>
             {!editMode && (
               <Button
                 variant="contained"
                 size="small"
                 startIcon={<FaEdit size={11} />}
                 onClick={() => setEditMode(true)}
                 sx={{
                   borderRadius: 99,
                   textTransform: "none",
                   fontWeight: 700,
                   fontSize: "0.78rem",
                   px: 2,
                   background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                   boxShadow: "0 4px 12px rgba(2,136,209,0.25)",
                   "&:hover": {
                     background: "linear-gradient(135deg, #0277bd, #0288D1)",
                   },
                 }}
               >
                 Edit
               </Button>
             )}
           </Stack>

           {editMode && (
             <>
               <Box sx={{ px: 2.5, py: 3 }}>
                 <Stack spacing={2}>
                   <Box>
                     <Typography
                       sx={{
                         fontSize: "0.68rem",
                         fontWeight: 700,
                         color: "#64748b",
                         textTransform: "uppercase",
                         letterSpacing: "0.6px",
                         mb: 0.75,
                       }}
                     >
                       Username
                     </Typography>
                     <TextField
                       fullWidth
                       size="small"
                       placeholder="Enter username"
                       value={newUsername}
                       onChange={(e) => setNewUsername(e.target.value)}
                       InputProps={{
                         startAdornment: (
                           <InputAdornment position="start">
                             <FaUser size={12} color="#0288D1" />
                           </InputAdornment>
                         ),
                       }}
                       sx={{
                         "& .MuiOutlinedInput-root": {
                           color: "#0c1a2e",
                           borderRadius: 2,
                           background: "#f8fafc",
                           "& fieldset": { borderColor: "rgba(2,136,209,0.2)" },
                           "&:hover fieldset": { borderColor: "#0288D1" },
                           "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                         },
                         "& input::placeholder": {
                           color: "#94a3b8",
                           fontSize: "0.85rem",
                         },
                       }}
                     />
                   </Box>

                   <Box>
                     <Typography
                       sx={{
                         fontSize: "0.68rem",
                         fontWeight: 700,
                         color: "#64748b",
                         textTransform: "uppercase",
                         letterSpacing: "0.6px",
                         mb: 0.75,
                       }}
                     >
                       Country
                     </Typography>
                     <Select
                       value={newCountry}
                       onChange={(e) => setNewCountry(e.target.value)}
                       displayEmpty
                       size="small"
                       fullWidth
                       sx={{
                         color: "#0c1a2e",
                         borderRadius: 2,
                         background: "#f8fafc",
                         "& .MuiOutlinedInput-notchedOutline": {
                           borderColor: "rgba(2,136,209,0.2)",
                         },
                         "&:hover .MuiOutlinedInput-notchedOutline": {
                           borderColor: "#0288D1",
                         },
                         "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                           borderColor: "#0288D1",
                         },
                         "& .MuiSvgIcon-root": { color: "#0288D1" },
                       }}
                     >
                       <MenuItem value="" disabled sx={{ color: "#94a3b8" }}>
                         Select country
                       </MenuItem>
                       {countries.map((c) => (
                         <MenuItem key={c.id} value={c.id}>
                           {c.name}
                         </MenuItem>
                       ))}
                     </Select>
                   </Box>

                   <Paper
                     elevation={0}
                     sx={{
                       p: 2,
                       borderRadius: 2,
                       background: newIsInst
                         ? "rgba(124,58,237,0.04)"
                         : "#f8fafc",
                       border: `1px solid ${
                         newIsInst ? "rgba(124,58,237,0.2)" : "#e2e8f0"
                       }`,
                       transition: "all 0.2s",
                     }}
                   >
                     <Stack
                       direction="row"
                       alignItems="center"
                       justifyContent="space-between"
                     >
                       <Stack direction="row" alignItems="center" spacing={1.5}>
                         <Box
                           sx={{
                             width: 30,
                             height: 30,
                             borderRadius: 1.5,
                             background: newIsInst
                               ? "rgba(124,58,237,0.12)"
                               : "rgba(100,116,139,0.08)",
                             border: `1px solid ${
                               newIsInst ? "rgba(124,58,237,0.25)" : "#e2e8f0"
                             }`,
                             display: "flex",
                             alignItems: "center",
                             justifyContent: "center",
                           }}
                         >
                           <FaSchool
                             size={13}
                             color={newIsInst ? "#7c3aed" : "#94a3b8"}
                           />
                         </Box>
                         <Box>
                           <Typography
                             sx={{
                               fontWeight: 700,
                               color: "#0c1a2e",
                               fontSize: "0.85rem",
                             }}
                           >
                             Register as Institution
                           </Typography>
                           <Typography
                             variant="caption"
                             sx={{ color: "#94a3b8" }}
                           >
                             Enable institution-level features
                           </Typography>
                         </Box>
                       </Stack>
                       <Switch
                         checked={newIsInst}
                         onChange={(e) => setNewIsInst(e.target.checked)}
                         sx={{
                           "& .MuiSwitch-switchBase.Mui-checked": {
                             color: "#7c3aed",
                           },
                           "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                             { backgroundColor: "#7c3aed" },
                         }}
                       />
                     </Stack>
                   </Paper>
                 </Stack>
               </Box>

               <Stack
                 direction="row"
                 spacing={1.5}
                 sx={{
                   px: 2.5,
                   py: 2,
                   borderTop: "1px solid #e2e8f0",
                   background: "#f8fafc",
                 }}
               >
                 <Button
                   variant="contained"
                   onClick={handleSaveProfile}
                   disabled={saving}
                   startIcon={saving ? null : <FaCheck size={12} />}
                   sx={{
                     borderRadius: 2,
                     textTransform: "none",
                     fontWeight: 700,
                     px: 3,
                     background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                     boxShadow: "0 4px 12px rgba(2,136,209,0.25)",
                     "&:hover": {
                       background: "linear-gradient(135deg, #0277bd, #0288D1)",
                     },
                     "&:disabled": { opacity: 0.5 },
                   }}
                 >
                   {saving ? (
                     <Stack direction="row" alignItems="center" spacing={1}>
                       <CircularProgress size={14} sx={{ color: "#fff" }} />
                       <span>Saving…</span>
                     </Stack>
                   ) : (
                     "Save Changes"
                   )}
                 </Button>
                 <Button
                   variant="outlined"
                   onClick={() => setEditMode(false)}
                   sx={{
                     borderRadius: 2,
                     textTransform: "none",
                     fontWeight: 700,
                     borderColor: "#e2e8f0",
                     color: "#64748b",
                     "&:hover": {
                       borderColor: "#94a3b8",
                       background: "#f1f5f9",
                     },
                   }}
                 >
                   Cancel
                 </Button>
               </Stack>
             </>
           )}
         </Paper>

         {/* ── Change Password ──────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(124,58,237,0.15)",
             boxShadow: "0 2px 12px rgba(124,58,237,0.06)",
             background: "#fff",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             justifyContent="space-between"
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: changePasswordMode ? "1px solid #e2e8f0" : "none",
               background:
                 "linear-gradient(135deg, rgba(124,58,237,0.05), rgba(168,85,247,0.03))",
             }}
           >
             <Stack direction="row" alignItems="center" spacing={1.5}>
               <Box
                 sx={{
                   width: 32,
                   height: 32,
                   borderRadius: 1.5,
                   background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   boxShadow: "0 4px 10px rgba(124,58,237,0.3)",
                 }}
               >
                 <FaKey size={13} color="#fff" />
               </Box>
               <Box>
                 <Typography
                   sx={{
                     fontWeight: 800,
                     color: "#0c1a2e",
                     fontSize: "0.92rem",
                   }}
                 >
                   Change Password
                 </Typography>
                 <Typography variant="caption" sx={{ color: "#64748b" }}>
                   Update your account security
                 </Typography>
               </Box>
             </Stack>
             {!changePasswordMode && (
               <Button
                 variant="outlined"
                 size="small"
                 startIcon={<FaKey size={11} />}
                 onClick={() => setChangePasswordMode(true)}
                 sx={{
                   borderRadius: 99,
                   textTransform: "none",
                   fontWeight: 700,
                   fontSize: "0.78rem",
                   px: 2,
                   borderColor: "rgba(124,58,237,0.3)",
                   color: "#7c3aed",
                   "&:hover": {
                     borderColor: "#7c3aed",
                     background: "rgba(124,58,237,0.05)",
                   },
                 }}
               >
                 Change
               </Button>
             )}
           </Stack>

           {changePasswordMode && (
             <>
               <Box sx={{ px: 2.5, py: 3 }}>
                 <Stack spacing={2}>
                   {[
                     {
                       label: "Current Password",
                       value: oldPassword,
                       setter: setOldPassword,
                     },
                     {
                       label: "New Password",
                       value: newPassword,
                       setter: setNewPassword,
                     },
                   ].map((f) => (
                     <Box key={f.label}>
                       <Typography
                         sx={{
                           fontSize: "0.68rem",
                           fontWeight: 700,
                           color: "#64748b",
                           textTransform: "uppercase",
                           letterSpacing: "0.6px",
                           mb: 0.75,
                         }}
                       >
                         {f.label}
                       </Typography>
                       <TextField
                         type="password"
                         fullWidth
                         size="small"
                         placeholder={`Enter ${f.label.toLowerCase()}`}
                         value={f.value}
                         onChange={(e) => f.setter(e.target.value)}
                         InputProps={{
                           startAdornment: (
                             <InputAdornment position="start">
                               <FaLock size={12} color="#7c3aed" />
                             </InputAdornment>
                           ),
                         }}
                         sx={{
                           "& .MuiOutlinedInput-root": {
                             color: "#0c1a2e",
                             borderRadius: 2,
                             background: "#f8fafc",
                             "& fieldset": {
                               borderColor: "rgba(124,58,237,0.2)",
                             },
                             "&:hover fieldset": { borderColor: "#7c3aed" },
                             "&.Mui-focused fieldset": {
                               borderColor: "#7c3aed",
                             },
                           },
                           "& input::placeholder": {
                             color: "#94a3b8",
                             fontSize: "0.85rem",
                           },
                         }}
                       />
                     </Box>
                   ))}
                 </Stack>
               </Box>
               <Stack
                 direction="row"
                 spacing={1.5}
                 sx={{
                   px: 2.5,
                   py: 2,
                   borderTop: "1px solid #e2e8f0",
                   background: "#f8fafc",
                 }}
               >
                 <Button
                   variant="contained"
                   onClick={handleChangePassword}
                   disabled={saving}
                   startIcon={saving ? null : <FaCheck size={12} />}
                   sx={{
                     borderRadius: 2,
                     textTransform: "none",
                     fontWeight: 700,
                     px: 3,
                     background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                     boxShadow: "0 4px 12px rgba(124,58,237,0.25)",
                     "&:hover": {
                       background: "linear-gradient(135deg, #6d28d9, #7c3aed)",
                     },
                     "&:disabled": { opacity: 0.5 },
                   }}
                 >
                   {saving ? (
                     <Stack direction="row" alignItems="center" spacing={1}>
                       <CircularProgress size={14} sx={{ color: "#fff" }} />
                       <span>Updating…</span>
                     </Stack>
                   ) : (
                     "Update Password"
                   )}
                 </Button>
                 <Button
                   variant="outlined"
                   onClick={() => setChangePasswordMode(false)}
                   sx={{
                     borderRadius: 2,
                     textTransform: "none",
                     fontWeight: 700,
                     borderColor: "#e2e8f0",
                     color: "#64748b",
                     "&:hover": {
                       borderColor: "#94a3b8",
                       background: "#f1f5f9",
                     },
                   }}
                 >
                   Cancel
                 </Button>
               </Stack>
             </>
           )}
         </Paper>

         {/* ── Affiliate Actions ────────────────────────────────────────── */}
         {profile.is_afliate && (
           <Paper
             elevation={0}
             sx={{
               borderRadius: 3,
               overflow: "hidden",
               border: "1px solid rgba(5,150,105,0.15)",
               boxShadow: "0 2px 12px rgba(5,150,105,0.06)",
               background: "#fff",
             }}
           >
             <Stack
               direction="row"
               alignItems="center"
               spacing={1.5}
               sx={{
                 px: 2.5,
                 py: 2,
                 borderBottom: "1px solid #e2e8f0",
                 background:
                   "linear-gradient(135deg, rgba(5,150,105,0.05), rgba(16,185,129,0.03))",
               }}
             >
               <Box
                 sx={{
                   width: 32,
                   height: 32,
                   borderRadius: 1.5,
                   background: "linear-gradient(135deg, #059669, #10b981)",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   boxShadow: "0 4px 10px rgba(5,150,105,0.3)",
                 }}
               >
                 <FaGift size={13} color="#fff" />
               </Box>
               <Box>
                 <Typography
                   sx={{
                     fontWeight: 800,
                     color: "#0c1a2e",
                     fontSize: "0.92rem",
                   }}
                 >
                   Affiliate Tools
                 </Typography>
                 <Typography variant="caption" sx={{ color: "#64748b" }}>
                   Manage your referral activity
                 </Typography>
               </Box>
             </Stack>

             <Box sx={{ px: 2.5, py: 2.5 }}>
               <Stack spacing={1.5}>
                 {[
                   {
                     label: "Promo Code",
                     icon: <FaTag size={13} />,
                     path: "/promo-code",
                     color: "#0288D1",
                     bg: "rgba(2,136,209,0.08)",
                     border: "rgba(2,136,209,0.2)",
                   },
                   {
                     label: "My Students",
                     icon: <FaUsers size={13} />,
                     path: "/students",
                     color: "#7c3aed",
                     bg: "rgba(124,58,237,0.08)",
                     border: "rgba(124,58,237,0.2)",
                   },
                 ].map((item) => (
                   <Paper
                     key={item.label}
                     elevation={0}
                     onClick={() => navigate(item.path)}
                     sx={{
                       px: 2,
                       py: 1.8,
                       borderRadius: 2,
                       background: item.bg,
                       border: `1px solid ${item.border}`,
                       cursor: "pointer",
                       transition: "all 0.2s",
                       "&:hover": {
                         transform: "translateX(4px)",
                         boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                       },
                     }}
                   >
                     <Stack
                       direction="row"
                       alignItems="center"
                       justifyContent="space-between"
                     >
                       <Stack direction="row" alignItems="center" spacing={1.5}>
                         <Box sx={{ color: item.color }}>{item.icon}</Box>
                         <Typography
                           sx={{
                             fontWeight: 700,
                             color: "#0c1a2e",
                             fontSize: "0.88rem",
                           }}
                         >
                           {item.label}
                         </Typography>
                       </Stack>
                       <FaChevronRight size={11} color="#94a3b8" />
                     </Stack>
                   </Paper>
                 ))}

                 <Paper
                   elevation={0}
                   onClick={() => navigate("/delete-account")}
                   sx={{
                     px: 2,
                     py: 1.8,
                     borderRadius: 2,
                     background: "rgba(239,68,68,0.05)",
                     border: "1px solid rgba(239,68,68,0.15)",
                     cursor: "pointer",
                     transition: "all 0.2s",
                     "&:hover": {
                       transform: "translateX(4px)",
                       background: "rgba(239,68,68,0.08)",
                     },
                   }}
                 >
                   <Stack
                     direction="row"
                     alignItems="center"
                     justifyContent="space-between"
                   >
                     <Stack direction="row" alignItems="center" spacing={1.5}>
                       <FaTrash size={13} color="#ef4444" />
                       <Typography
                         sx={{
                           fontWeight: 700,
                           color: "#ef4444",
                           fontSize: "0.88rem",
                         }}
                       >
                         Delete Account
                       </Typography>
                     </Stack>
                     <FaChevronRight size={11} color="#fca5a5" />
                   </Stack>
                 </Paper>
               </Stack>
             </Box>
           </Paper>
         )}

         {/* ── Promo Code Entry ─────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(2,136,209,0.15)",
             boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
             background: "#fff",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             spacing={1.5}
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: "1px solid #e2e8f0",
               background:
                 "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
             }}
           >
             <Box
               sx={{
                 width: 32,
                 height: 32,
                 borderRadius: 1.5,
                 background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
               }}
             >
               <FaTicketAlt size={13} color="#fff" />
             </Box>
             <Box>
               <Typography
                 sx={{ fontWeight: 800, color: "#0c1a2e", fontSize: "0.92rem" }}
               >
                 Referral Code
               </Typography>
               <Typography variant="caption" sx={{ color: "#64748b" }}>
                 Enter a promo code from a friend
               </Typography>
             </Box>
           </Stack>

           <Box sx={{ px: 2.5, py: 3 }}>
             {!referrerName ? (
               <Stack spacing={1.5}>
                 <Typography
                   sx={{
                     fontSize: "0.68rem",
                     fontWeight: 700,
                     color: "#64748b",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                   }}
                 >
                   Promo Code
                 </Typography>
                 <Stack direction="row" spacing={1.5}>
                   <TextField
                     fullWidth
                     size="small"
                     placeholder="e.g. JOHN2024"
                     value={promoCode}
                     onChange={(e) => setPromoCode(e.target.value)}
                     InputProps={{
                       startAdornment: (
                         <InputAdornment position="start">
                           <FaTag size={12} color="#0288D1" />
                         </InputAdornment>
                       ),
                     }}
                     sx={{
                       "& .MuiOutlinedInput-root": {
                         color: "#0c1a2e",
                         borderRadius: 2,
                         background: "#f8fafc",
                         "& fieldset": { borderColor: "rgba(2,136,209,0.2)" },
                         "&:hover fieldset": { borderColor: "#0288D1" },
                         "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                       },
                       "& input::placeholder": {
                         color: "#94a3b8",
                         fontSize: "0.85rem",
                       },
                     }}
                   />
                   <Button
                     variant="contained"
                     onClick={handleSavePromo}
                     disabled={loadingPromo}
                     sx={{
                       borderRadius: 2,
                       textTransform: "none",
                       fontWeight: 700,
                       px: 3,
                       flexShrink: 0,
                       background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                       boxShadow: "0 4px 12px rgba(2,136,209,0.25)",
                       "&:hover": {
                         background:
                           "linear-gradient(135deg, #0277bd, #0288D1)",
                       },
                       "&:disabled": { opacity: 0.5 },
                     }}
                   >
                     {loadingPromo ? (
                       <CircularProgress size={16} sx={{ color: "#fff" }} />
                     ) : (
                       "Apply"
                     )}
                   </Button>
                 </Stack>
                 {error && (
                   <Stack direction="row" alignItems="center" spacing={1}>
                     <FaExclamationTriangle size={12} color="#ef4444" />
                     <Typography
                       sx={{
                         color: "#ef4444",
                         fontSize: "0.8rem",
                         fontWeight: 600,
                       }}
                     >
                       {error}
                     </Typography>
                   </Stack>
                 )}
               </Stack>
             ) : (
               <Paper
                 elevation={0}
                 sx={{
                   p: 2,
                   borderRadius: 2,
                   background: "rgba(5,150,105,0.05)",
                   border: "1px solid rgba(5,150,105,0.2)",
                 }}
               >
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                   <Box
                     sx={{
                       width: 32,
                       height: 32,
                       borderRadius: "50%",
                       background: "rgba(5,150,105,0.1)",
                       border: "1px solid rgba(5,150,105,0.2)",
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                     }}
                   >
                     <FaCheck size={13} color="#059669" />
                   </Box>
                   <Box>
                     <Typography
                       sx={{
                         fontSize: "0.65rem",
                         fontWeight: 700,
                         color: "#94a3b8",
                         textTransform: "uppercase",
                         letterSpacing: "0.5px",
                       }}
                     >
                       Referred by
                     </Typography>
                     <Typography
                       sx={{
                         fontWeight: 800,
                         color: "#059669",
                         fontSize: "0.92rem",
                       }}
                     >
                       {referrerName}
                     </Typography>
                   </Box>
                 </Stack>
               </Paper>
             )}
           </Box>
         </Paper>
       </Stack>
     </Container>
   </Box>
 );
};

export default ProfilePageFile;
